import React, { useEffect } from "react"
import { ReactSVG } from "react-svg"

import { graphql } from "gatsby"
import { ArticleStructuredTextFieldsFragment } from "../../../../../../graphql-types"

import * as styles from "./article-structured-text-content.module.scss"
import { elementOffset, transformItem } from "../../../functions/functions"
import useWindowWidth from "../../../../../hooks/use-window-width"
import StructuredTextContent from "../structured-text-content/structured-text-content"

type ArticleStructuredTextContentProps = {
  data: ArticleStructuredTextFieldsFragment
}

const ArticleStructuredTextContent: React.FC<ArticleStructuredTextContentProps> = ({
  data: { structuredText, image, keyFigureElement, textWithIconElement },
}) => {
  const isMobile = useWindowWidth() <= 825
  const keyFigure = keyFigureElement[0]
  const textWithIcon = textWithIconElement[0]
  
  useEffect(() => {
    handleScrollAnimation()
  }, [])

  const displayImg = (img, classes?): JSX.Element => {
    if (!img) return

    return img.format === "svg" ? (
      <div className={classes}>
        <ReactSVG src={img.url} title={img.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
    ) : (
      <div className={classes}>
        <img src={img.url} alt={img.alt || ""} title={img.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
    )
  }

  const handleScrollAnimation = () => {
    document.addEventListener("scroll", () => {
      const container = document.getElementsByClassName(styles.container)

      Array.from(container).forEach(artContainer => {
        if (window.scrollY >= elementOffset(artContainer).top - 700) {
          transformItem(artContainer.getElementsByClassName(styles.structuredText)[0], 400)
          transformItem(artContainer.getElementsByClassName(styles.optionalContainer)[0], 800)
        }
      })
    })
  }

  if (isMobile) {
    return (
      <div className={styles.container}>
        <div className={styles.OptionalImageContainer}>
          {(image || textWithIcon) && (
            <figure role="group" className={styles.imageContainer}>
              {image ? (
                <>
                  {displayImg(image, styles.image)}
                  <figcaption className={styles.imageTitle}>{image.title}</figcaption>
                </>
              ) : (
                <>
                  {displayImg(textWithIcon.icon, styles.imageIcon)}
                  <figcaption className={styles.imageTitle}>{textWithIcon.text}</figcaption>
                </>
              )}
            </figure>
          )}
        </div>

        {(keyFigure) && (
          <div className={styles.optionalContainer}>
            {keyFigure && (
              <div>
                <div className={styles.keyContainer}>
                  {keyFigure.symbolBeforeNumber && <p className={styles.symbol}>{keyFigure.symbolBeforeNumber}</p>}
                  <p className={styles.number}>{keyFigure.number}</p>
                  {keyFigure.symbolAfterNumber && <p className={styles.symbol}>{keyFigure.symbolAfterNumber}</p>}
                  {displayImg(keyFigure.icon, styles.keyFigureIcon)}
                </div>
                <p className={styles.subtitle}>{keyFigure.subtitle}</p>
              </div>
            )}
          </div>
        )}

        <div className={styles.articleStructuredText}>
          <StructuredTextContent data={structuredText.value} isNotRichContent={true} />
        </div>
      </div>
    )
  } else {
    return (
      <div className={styles.container}>
        <div className={styles.articleStructuredText}>
          <div className={styles.structuredText}>
            <StructuredTextContent data={structuredText.value} isNotRichContent={true} />
          </div>
          {keyFigure ? (
            <div className={styles.optionalContainer}>
              {keyFigure && (
                <div style={{ position: "relative" }}>
                  <div className={styles.keyContainer}>
                    {keyFigure.symbolBeforeNumber && <p className={styles.symbol}>{keyFigure.symbolBeforeNumber}</p>}
                    <p className={styles.number}>{keyFigure.number}</p>
                    {keyFigure.symbolAfterNumber && <p className={styles.symbol}>{keyFigure.symbolAfterNumber}</p>}
                    {displayImg(keyFigure.icon, styles.keyFigureIcon)}
                  </div>
                  <p className={styles.subtitle}>{keyFigure.subtitle}</p>
                </div>
              )}
            </div>
          ) : (
            (image || textWithIcon) && (
              <div className={styles.optionalContainer}>
                {image ? (
                  <figure role="group" className={styles.imageContainer}>
                    {displayImg(image, styles.image)}
                    <figcaption className={styles.imageTitle}>{image.title}</figcaption>
                  </figure>
                ) : (
                  textWithIcon && (
                    <figure role="group" className={styles.imageContainer}>
                      {displayImg(textWithIcon.icon, styles.imageIcon)}
                      <figcaption className={styles.imageTitle}>{textWithIcon.text}</figcaption>
                    </figure>
                  )
                )}
              </div>
            )
          )}
        </div>
      </div>
    )
  }
}

export const fragment = graphql`
  fragment ArticleStructuredTextFields on DatoCmsArticleStructuredText {
    __typename
    id
    structuredText {
      value
    }
    image {
      alt
      format
      title
      url
    }
    keyFigureElement {
      ...KeyFigureFields
    }
    textWithIconElement {
      text
      icon {
        alt
        format
        title
        url
      }
    }
    backgroundColor {
      rgb
    }
  }
`

export default ArticleStructuredTextContent
